import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { RequireHuurder, RequireVerhuurder } from "./components/ProtectedRoutes";
import HomePage from "./pages/HomePage";
import ListingsPage from "./pages/ListingsPage";
import ListingDetailPage from "./pages/ListingDetailPage";
import DashboardPage from "./pages/DashboardPage";
import PremiumPage from "./pages/PremiumPage";
import KamerVerhurenPage from "./pages/KamerVerhurenPage";
import AdminPage from "./pages/AdminPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import SafetyPage from "./pages/SafetyPage";
import FAQPage from "./pages/FAQPage";
import PricingPage from "./pages/PricingPage";
import LandlordTipsPage from "./pages/LandlordTipsPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import BlogPage, { BlogPostPage } from "./pages/BlogPage";
import CareersPage from "./pages/CareersPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import SwipeMatchPage from "./pages/SwipeMatchPage";
import SwipeInterfacePage from "./pages/SwipeInterfacePage";
import LandlordOnboardingPage from "./pages/LandlordOnboardingPage";
import VerhuurderDashboardPage from "./pages/VerhuurderDashboardPage";
import VerhuurderVoorkeurenPage from "./pages/VerhuurderVoorkeurenPage";
import VerhuurderAccountPage from "./pages/VerhuurderAccountPage";
import EditListingPage from "./pages/EditListingPage";
import ProfilePage from "./pages/ProfilePage";
import UpgradeForListingPage from "./pages/UpgradeForListingPage";
import UpgradeForMatchPage from "./pages/UpgradeForMatchPage";
import ChatPage from "./pages/ChatPage";
import BoostListingPage from "./pages/BoostListingPage";
import BoostSuccessPage from "./pages/BoostSuccessPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/ForgotPasswordPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import AuthCallback from "./pages/AuthCallback";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <LanguageProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              {/* Auth Callback - must be before other routes */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              
              {/* Main Pages */}
              <Route path="/" element={<HomePage />} />
              <Route path="/aanbod" element={<ListingsPage />} />
              <Route path="/listings" element={<ListingsPage />} />
              <Route path="/woning/:id" element={<ListingDetailPage />} />
              <Route path="/listing/:id" element={<ListingDetailPage />} />
              
              {/* Chat - protected, only for huurders */}
              <Route path="/chat/:listingId" element={<RequireHuurder><ChatPage /></RequireHuurder>} />
              
              {/* For Renters (Huurders) ONLY */}
              <Route path="/dashboard" element={<RequireHuurder><DashboardPage /></RequireHuurder>} />
              <Route path="/mijn-account" element={<RequireHuurder><DashboardPage /></RequireHuurder>} />
              <Route path="/profiel" element={<RequireHuurder><ProfilePage /></RequireHuurder>} />
              <Route path="/profile" element={<RequireHuurder><ProfilePage /></RequireHuurder>} />
              <Route path="/swipematch" element={<RequireHuurder><SwipeMatchPage /></RequireHuurder>} />
              <Route path="/swipematch/swipe" element={<RequireHuurder><SwipeInterfacePage /></RequireHuurder>} />
              <Route path="/premium" element={<PremiumPage />} />
              <Route path="/upgrade/:listingId" element={<RequireHuurder><UpgradeForListingPage /></RequireHuurder>} />
              <Route path="/upgrade-match/:listingId" element={<RequireHuurder><UpgradeForMatchPage /></RequireHuurder>} />
              <Route path="/hoe-werkt-het" element={<HowItWorksPage />} />
              <Route path="/veiligheid" element={<SafetyPage />} />
              <Route path="/faq" element={<FAQPage />} />
              
              {/* For Landlords (Verhuurders) ONLY */}
              <Route path="/verhuren" element={<Navigate to="/verhuurder/onboarding" replace />} />
              <Route path="/rent-out" element={<Navigate to="/verhuurder/onboarding" replace />} />
              <Route path="/kamer-verhuren" element={<KamerVerhurenPage />} />
              <Route path="/verhuurder/onboarding" element={<RequireVerhuurder><LandlordOnboardingPage /></RequireVerhuurder>} />
              <Route path="/verhuurder/dashboard" element={<RequireVerhuurder><VerhuurderDashboardPage /></RequireVerhuurder>} />
              <Route path="/verhuurder/voorkeuren" element={<RequireVerhuurder><VerhuurderVoorkeurenPage /></RequireVerhuurder>} />
              <Route path="/verhuurder/account" element={<RequireVerhuurder><VerhuurderAccountPage /></RequireVerhuurder>} />
              <Route path="/verhuurder/woning/:id/bewerk" element={<RequireVerhuurder><EditListingPage /></RequireVerhuurder>} />
              <Route path="/verhuurder/boost/:listingId" element={<RequireVerhuurder><BoostListingPage /></RequireVerhuurder>} />
              <Route path="/verhuurder/boost/success" element={<RequireVerhuurder><BoostSuccessPage /></RequireVerhuurder>} />
              <Route path="/tarieven" element={<PricingPage />} />
              <Route path="/tips-verhuurders" element={<LandlordTipsPage />} />
              <Route path="/verhuurder/tips" element={<LandlordTipsPage />} />
              
              {/* Company */}
              <Route path="/over-ons" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/carriere" element={<CareersPage />} />
              
              {/* Legal */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/voorwaarden" element={<TermsPage />} />
              <Route path="/cookies" element={<PrivacyPage />} />
              
              {/* Auth */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/registreren" element={<RegisterPage />} />
              <Route path="/wachtwoord-vergeten" element={<ForgotPasswordPage />} />
              <Route path="/wachtwoord-reset" element={<ResetPasswordPage />} />
              <Route path="/verificatie" element={<EmailVerificationPage />} />
              
              {/* Admin */}
              <Route path="/admin" element={<AdminPage />} />
              
              {/* Legacy redirects */}
              <Route path="/over-roomhub" element={<Navigate to="/over-ons" replace />} />
              <Route path="/tips" element={<Navigate to="/tips-verhuurders" replace />} />
              <Route path="/support" element={<Navigate to="/contact" replace />} />
              <Route path="/premium-account-betaling" element={<Navigate to="/premium" replace />} />
              <Route path="/verhuurder/swipe" element={<Navigate to="/verhuurder/dashboard" replace />} />
              <Route path="/woning/:id/swipe" element={<Navigate to="/swipematch/swipe" replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </LanguageProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
